import { useCallback, useRef } from "react";

import type {
  dashboardApiEndpoints,
  ExtractArgsFromQueryKey,
  ExtractFetcherReturn,
  FilteredDashboardApiEndpointKeys,
} from "@dashboardApi/apis";
import { QueryObserver, useQueryClient } from "@tanstack/react-query";

import { suppressFetchErrorModalForSignal } from "@lib/fetch/fetchErrorModalSuppression";
import { useAppGroupId } from "@services/useAppGroupId";
import { useInEditorDomain } from "@services/useInEditorDomain";

import { useDashboardApi } from "./useDashboardApi";
import { useEditorDomainApiQueryFetcher } from "./useEditorDomainApiQueryFetcher";

/**
 * Hook for making imperative GET requests to dashboard API endpoints,
 * intended for use inside Operator tool callbacks.
 *
 * Uses a QueryObserver under the hood so results are cached and shared with
 * `useDashboardApiQuery` calls for the same endpoint. Unlike
 * `queryClient.fetchQuery`, which joins an in-flight query without registering
 * as an observer, this approach keeps the observer count above zero until the
 * fetch completes — preventing React Query from cancelling a shared fetch when
 * a competing `useQuery` hook unmounts.
 *
 * @example
 * ```ts
 * const { fetch: fetchSegment } = useDashboardApiOperatorQuery("/engagement/segment_data");
 *
 * // inside a tool callback:
 * const result = await fetchSegment(segmentId, appGroupId, false, false);
 * ```
 */
export const useDashboardApiOperatorQuery = <
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, "GET">,
>(
  endpoint: Endpoint
) => {
  const { GET: fetcherFunction } = useDashboardApi(endpoint);
  const queryClient = useQueryClient();
  const appGroupId = useAppGroupId();
  // In the editor-domain iframe the backend is unreachable directly; route
  // through the parent's postMessage fetcher like useDashboardApiQuery does.
  // Ref'd because the fetcher hook returns a new closure per render.
  const inEditorDomain = useInEditorDomain();
  const editorDomainFetcher = useEditorDomainApiQueryFetcher(endpoint);
  const editorDomainFetcherRef = useRef(editorDomainFetcher);
  editorDomainFetcherRef.current = editorDomainFetcher;

  const fetch = useCallback(
    (
      ...params: ExtractArgsFromQueryKey<typeof dashboardApiEndpoints, Endpoint>
    ): Promise<ExtractFetcherReturn<typeof dashboardApiEndpoints, Endpoint>> => {
      const queryKey = [{ cacheKey: endpoint, appGroupId }, ...params];

      return new Promise<ExtractFetcherReturn<typeof dashboardApiEndpoints, Endpoint>>((resolve, reject) => {
        let settled = false;
        const observer = new QueryObserver(queryClient, {
          queryKey,
          queryFn: (queryContext) => {
            suppressFetchErrorModalForSignal(queryContext.signal);
            return (fetcherFunction as any)(
              queryContext,
              inEditorDomain ? editorDomainFetcherRef.current : undefined
            );
          },
          retry: false,
        });

        const unsubscribe = observer.subscribe((result) => {
          if (settled) {
            return;
          }
          if (result.status === "error") {
            settled = true;
            unsubscribe();
            reject(result.error);
          } else if (result.status === "success" && !result.isFetching) {
            settled = true;
            unsubscribe();
            resolve(result.data as ExtractFetcherReturn<typeof dashboardApiEndpoints, Endpoint>);
          }
        });
      });
    },
    [endpoint, appGroupId, fetcherFunction, queryClient, inEditorDomain]
  );

  return { fetch };
};
